import type { Deck } from "@/services/api/flashcards/get-all"
import { Play, SquarePen } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useNoLifeAlert } from "@/modules/app/stores/use-no-life-alert"
import useCanContinueLearning from "@/modules/user/hooks/use-can-continue-learning"
import { useUpdateFlashcardModal } from "@/modules/flashcards/stores/use-update-flashcard-modal-store"
import useEndFreeTrialModal from "@/modules/plans/stores/use-end-free-trial"
import { useFinishFreeTrial } from "@/modules/user/hooks/use-finish-free-trial"
import mixpanelClient from "@/services/mixpanel"
import { useUser } from "@/modules/user/stores"
import { useRecordLifeUsage } from "@/modules/user/queries/use-record-life-usage"

interface FlashcardDeckListProps {
  decks: Deck[]
}

const FlashcardDeckList = ({ decks }: FlashcardDeckListProps) => {
  const navigate = useNavigate()
  const canContinueLearning = useCanContinueLearning()
  const { setIsOpen: openNoLifeAlert } = useNoLifeAlert()
  const { openModal: openUpdateFlashcardModal } = useUpdateFlashcardModal()
  const { isTrialFinished } = useFinishFreeTrial()
  const { onToggle } = useEndFreeTrialModal()
  const { user, lifeUsage } = useUser()
  const { mutate: recordLifeUsage } = useRecordLifeUsage()

  const handleOnClickDeck = (deckId: string) => {
    if (isTrialFinished) {
      onToggle()
      return
    }
    mixpanelClient.flashcards.startFlashcard({
      startDate: new Date().toISOString(),
      userPlan: user.activePlan.name,
      userName: user.name,
      userEmail: user.email,
    })
    if (lifeUsage.list.length < 5) {
      recordLifeUsage({ activityCategory: "FLASHCARD" })
    }
    if (canContinueLearning) {
      navigate(`/flashcards/${deckId}`)
      return
    }
    openNoLifeAlert(true)
  }

  const handleOnClickPen = (deckId: string) => {
    openUpdateFlashcardModal(deckId)
  }

  return decks.length > 0 ? (
    <div className="flex w-full flex-col gap-4">
      {decks.map((deck) => {
        return (
          <div
            className="flex w-full cursor-pointer gap-4 rounded-xl bg-orange-50 p-4"
            key={deck.id}
          >
            <div
              className="flex w-full max-w-full flex-col justify-between gap-4 overflow-hidden"
              onClick={() => handleOnClickDeck(deck.id)}
            >
              <p className="truncate text-xs text-gray-400">{deck.name}</p>
              <p className="text-xs text-gray-400">Cartas: {deck.cardsAmount}</p>
            </div>
            <div className="flex w-[30px] shrink-0 items-center justify-center">
              <Play onClick={() => handleOnClickDeck(deck.id)} />
            </div>
            <div className="flex w-[30px] shrink-0 items-center justify-center">
              <SquarePen onClick={() => handleOnClickPen(deck.id)} />
            </div>
          </div>
        )
      })}
    </div>
  ) : (
    <div className="w-full rounded-xl bg-gray-100 p-4 text-center">
      <p className="text-base text-neutral-400">No hay mazos!</p>
    </div>
  )
}

export default FlashcardDeckList
